import Button from 'components/core/button'
import IconButton from 'lib/components/buttons/icon-button'
import Modal, { BaseModalProps } from 'lib/components/modals/modal'
import { X } from 'lucide-react'
import TextInput from 'lib/components/text-input/text-input'
import SelectBox, { SelectBoxOption, SelectBoxValue } from 'lib/components/dropdown/select-box'
import { Company } from 'interfaces/company'
import countryList from 'react-select-country-list'
import { useEffect, useMemo, useState } from 'react'
import { states } from 'lib/util/states'
import { request } from 'lib/api/fetch-api'
import { toast } from 'lib/components/toast/toast'
import { useMutation } from '@tanstack/react-query'

export default function BillingAddressModal({
  company,
  open,
  setCompany,
  setOpen,
}: {
  company: Company
  open: BaseModalProps['open']
  setCompany: (company: Company) => void
  setOpen: BaseModalProps['setOpen']
}) {
  const countryData = useMemo(() => countryList().getData(), [])
  const countryOptions: SelectBoxOption[] = useMemo(
    () =>
      countryData.map((option) => ({
        ...option,
        label: option.label,
        displayElement: <span>{option.label}</span>,
      })),
    [countryData],
  )
  const [formData, setFormData] = useState({
    address1: company.address1,
    address2: company.address2,
    city: company.city,
    state: company.state,
    zip: company.zip,
    country: company.country,
  })
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const selectedCountry = countryData.find((option) => option.value === formData.country)?.value
  const selectedState = states.find((option) => option.value === formData.state)?.value

  useEffect(() => {
    if (formData.country !== 'US') {
      setFormData((prev) => ({
        ...prev,
        state: '',
      }))
    }
  }, [formData.country])

  const { mutate: updateCompanyAddress } = useMutation({
    mutationFn: (updatedData: typeof formData) =>
      request({
        endpoint: 'updateCompany',
        body: updatedData,
        query: { id: company.id },
      }),
    onSuccess: (response) => {
      const updatedCompany = response
      setCompany(updatedCompany)
      setOpen(false)
      toast.success('Company address updated successfully')
    },
    onError: (error) => {
      console.error(error)
      toast.error('Error updating company address')
    },
  })

  const validateForm = () => {
    if (!validateAddressForm()) {
      setShowValidationErrors(true)
      toast.error('Please fill out all required fields')
      return false
    }

    return true
  }

  const validateAddressForm = () => {
    return !!(
      formData.address1.length > 0 &&
      formData.city.length > 0 &&
      formData.zip.length > 0 &&
      formData.country.length > 0 &&
      (formData.country !== 'US' || formData.state.length > 0)
    )
  }

  const submitForm = () => {
    if (!validateForm()) return

    updateCompanyAddress(formData)
  }

  return (
    <Modal open={open} setOpen={setOpen} size="lg">
      <Modal.Header as="div">
        <div className="tw-flex tw-items-center tw-justify-between">
          <h4>Update Address</h4>
          <IconButton color="secondary" onClick={() => setOpen(false)} data-testid="close-modal-button">
            <X />
          </IconButton>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="tw-mt-4 tw-flex tw-w-full tw-flex-col">
          <div className="tw-flex tw-w-full tw-gap-2">
            <div className="tw-w-3/4">
              <TextInput
                label="Street Address"
                placeholder="Ex. 1234 Main St"
                defaultValue={formData.address1}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    address1: e.target.value,
                  }))
                }}
                className="tw-mb-4"
                error={showValidationErrors && formData.address1.length === 0}
                required
              />
            </div>
            <div className="tw-w-1/4">
              <TextInput
                label="Apt / Unit"
                placeholder="Ex. Apt 101"
                defaultValue={formData.address2}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    address2: e.target.value,
                  }))
                }}
                className="tw-mb-4"
              />
            </div>
          </div>
          <TextInput
            label="City"
            placeholder="Ex. Scottsdale"
            defaultValue={formData.city}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                city: e.target.value,
              }))
            }}
            error={showValidationErrors && formData.city.length === 0}
            required
          />
          {formData.country === 'US' && (
            <SelectBox
              options={states.map((state) => ({
                label: state.label,
                value: state.value,
                displayElement: <span>{state.label}</span>,
              }))}
              handleChange={(selectedValue: SelectBoxValue) => {
                const selectedOption = states.find((option) => option.value === selectedValue)
                setFormData((prev) => ({
                  ...prev,
                  state: selectedOption?.value || null,
                }))
              }}
              selectedValue={selectedState}
              label="State"
              labelClassName="tw-text-neutral-800 tw-mb-0"
              className="tw-mb-4"
              error={showValidationErrors && formData.state.length === 0}
              required
            />
          )}
          <TextInput
            label="Zip Code"
            placeholder="Ex. 12345"
            defaultValue={formData.zip}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                zip: e.target.value,
              }))
            }}
            error={showValidationErrors && formData.zip.length === 0}
            required
          />
          <SelectBox
            options={countryOptions}
            handleChange={(selectedValue: SelectBoxValue) => {
              const selectedOption = countryOptions.find((option) => option.value === selectedValue)
              setFormData((prev) => ({
                ...prev,
                country: (selectedOption?.value as string) || '',
              }))
            }}
            selectedValue={selectedCountry}
            label="Country"
            labelClassName="tw-text-neutral-800 tw-mb-0"
            error={showValidationErrors && formData.country.length === 0}
            required
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="tw-mt-8 tw-flex tw-w-full tw-justify-between">
          <div></div>
          <div className="tw-flex tw-justify-end">
            <Button
              color="neutral"
              onClick={() => {
                setOpen(false)
              }}
              className="tw-mr-2"
            >
              Cancel
            </Button>
            <Button color="purple" onClick={submitForm}>
              Update
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
